import AppDispatcher           from '~/dispatchers/app_dispatcher';
import APIRequest              from '~/lib/api_request';
import JobActionTypes          from '~/types/job_action_types';
import GlobalContainer         from '~/components/global_container';
import { getRestrictionScope } from '~/helpers/staffRestrictionHelper';
import tableConfig             from '~/components/pages/agent_dashboard/table_config';
import BulkSelectActions       from '~/actions/bulk_select_actions';

let jobsRequest;
let createJobRequest;
let updateJobRequest;
let deleteJobRequest;
let bulkUpdateJobsRequest;
let bulkDeleteJobsRequest;
let loadJobFormDropdownsRequest;
let loadCandidatesRequest;
let loadLeadJobApplicationsRequest;
let bulkRemoveCandidatesFromJobRequest;
let bulkUpdateCandidatesFromJobRequest;

const jobTableScopeRequest = {};

const JobActions = {
  defaultOptions() {
    const staffRestriction = getRestrictionScope();

    return {
      ownerScope: localStorage.ownerScope || staffRestriction || 'all',
      page:       1,
      size:       parseInt(localStorage.jobScopeSize, 10) || 10,
      searchData: {},
    };
  },

  loadJobs(table = tableConfig.jobsTable(), options = {}) {
    const opts = Object.assign(this.defaultOptions(), options);

    const query = {
      job_scope: table.scope,
      scope:     opts.ownerScope,
      page:      opts.page,
      per:       opts.size,
      s:         opts.searchData || {},
    };

    const { sortColumn, sortDirection } = opts;
    if (sortColumn && sortDirection) {
      query.job_sort = `${sortColumn}-${sortDirection}`;
    }

    AppDispatcher.dispatch({
      type: JobActionTypes.LOAD_JOBS,
      table,
    });

    if (jobTableScopeRequest[table.scope]) {
      APIRequest.abort(jobTableScopeRequest[table.scope]);
    }

    jobTableScopeRequest[table.scope] = APIRequest.get({
      resource: '/v1/jobs',
      data:     query,
    });

    jobTableScopeRequest[table.scope].end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.LOAD_JOBS_FAIL,
          table,
          error,
        });
        GlobalContainer.notify('Failed to load jobs', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:     JobActionTypes.LOAD_JOBS_DONE,
        table,
        response: response.body,
        query,
      });
    });
  },
  loadLeadJobApplications(options = {}) {
    const {
      page = 1, per = 5, s = {}, job_application_sort,
    } = options;

    const requestData = {
      page,
      per,
      s,
      job_application_sort,
    };

    if (!s.lead_id) {
      return;
    }

    APIRequest.abort(loadLeadJobApplicationsRequest);

    AppDispatcher.dispatch({
      type: JobActionTypes.LOAD_LEAD_JOB_APPLICATIONS,
    });

    loadLeadJobApplicationsRequest = APIRequest.get({
      resource: '/v1/jobs/lead_job_applications',
      data:     requestData,
    });

    loadLeadJobApplicationsRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.LOAD_LEAD_JOB_APPLICATIONS_FAIL,
          error,
        });
        return;
      }

      AppDispatcher.dispatch({
        type:     JobActionTypes.LOAD_LEAD_JOB_APPLICATIONS_DONE,
        response: response.body,
        query:    requestData,
      });
    });
  },

  loadJob(jobId) {
    APIRequest.abort(jobsRequest);

    AppDispatcher.dispatch({
      type: JobActionTypes.LOAD_JOB,
      jobId,
    });

    // Single endpoint handles both authenticated (full data) and
    // public (active jobs only, restricted fields) access
    jobsRequest = APIRequest.get({
      resource: `/v1/jobs/${jobId}`,
    });

    jobsRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.LOAD_JOB_FAIL,
          error,
        });
        if (error.status !== 404) {
          GlobalContainer.notify('Failed to load job', 'error');
        }
        return;
      }

      AppDispatcher.dispatch({
        type: JobActionTypes.LOAD_JOB_DONE,
        job:  response.body,
      });
    });
  },

  createJob(job, { page = 1, loadJobs = true, searchData = {} } = {}, table = null) {
    APIRequest.abort(createJobRequest);

    AppDispatcher.dispatch({
      type: JobActionTypes.CREATE_JOB,
      job,
    });

    createJobRequest = APIRequest.post({
      resource: '/v1/jobs',
      data:     { job },
    });

    createJobRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.CREATE_JOB_FAIL,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to create the job. Please try again later.', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type: JobActionTypes.CREATE_JOB_DONE,
        job:  response.body,
      });

      GlobalContainer.notify('Job saved successfully', 'success');

      if (loadJobs && table) {
        JobActions.loadJobs(table, {
          page,
          searchData,
          ownerScope: localStorage.ownerScope || 'all',
        });
      }
    });
  },

  updateJob(job, { page = 1, loadJobs = true, searchData = {} } = {}, table = null) {
    APIRequest.abort(updateJobRequest);

    AppDispatcher.dispatch({
      type: JobActionTypes.UPDATE_JOB,
      job,
    });

    updateJobRequest = APIRequest.put({
      resource: `/v1/jobs/${job.id}`,
      data:     { job },
    });

    updateJobRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.UPDATE_JOB_FAIL,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to update the job. Please try again later.', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type: JobActionTypes.UPDATE_JOB_DONE,
        job:  response.body,
      });

      GlobalContainer.notify('Job saved successfully', 'success');

      if (loadJobs && table) {
        JobActions.loadJobs(table, {
          page,
          searchData,
          ownerScope: localStorage.ownerScope || 'all',
        });
      }
    });
  },

  deleteJob(job, { page = 1, loadJobs = true, searchData = {} } = {}, table = null) {
    APIRequest.abort(deleteJobRequest);

    AppDispatcher.dispatch({
      type: JobActionTypes.DELETE_JOB,
      job,
    });

    deleteJobRequest = APIRequest.delete({
      resource: `/v1/jobs/${job.id}`,
    });

    deleteJobRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.DELETE_JOB_FAIL,
          error,
        });

        GlobalContainer.notify('Failed to delete the job. Please try again later.', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:  JobActionTypes.DELETE_JOB_DONE,
        jobId: job.id,
      });

      GlobalContainer.notify('Job has been deleted', 'success');

      if (table) {
        BulkSelectActions.deselectJobItem(table, job);
      }

      if (loadJobs && table) {
        JobActions.loadJobs(table, {
          page,
          searchData,
          ownerScope: localStorage.ownerScope || 'all',
        });
      }
    });
  },

  cloneJob(jobId, cloneData, { page = 1, searchData = {} } = {}, table = null) {
    AppDispatcher.dispatch({
      type: JobActionTypes.CLONE_JOB,
    });

    const cloneJobRequest = APIRequest.post({
      resource: `/v1/jobs/${jobId}/clone`,
      data:     { job: cloneData },
    });

    cloneJobRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.CLONE_JOB_FAIL,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to clone job. Please try again later.', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type: JobActionTypes.CLONE_JOB_DONE,
        job:  response.body,
      });

      GlobalContainer.notify('Job cloned successfully', 'success');

      if (table) {
        JobActions.loadJobs(table, {
          page,
          searchData,
          ownerScope: localStorage.ownerScope || 'all',
        });
      }
    });
  },

  loadJobFormDropdowns() {
    APIRequest.abort(loadJobFormDropdownsRequest);
    const include = [
      'job_types',
      'categories',
      'educations',
      'experiences',
      'payment_types',
      'currencies',
      'statuses',
    ];
    loadJobFormDropdownsRequest = APIRequest.get({
      resource: `/v1/jobs/dropdown_data?include[]=${include.join('&include[]=')}`,
    });

    loadJobFormDropdownsRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.LOAD_JOB_FORM_DROPDOWNS_FAIL,
          error,
        });
        GlobalContainer.notify('Failed to load job form dropdowns', 'error');
        return;
      }

      AppDispatcher.dispatch({
        type:      JobActionTypes.LOAD_JOB_FORM_DROPDOWNS_DONE,
        dropdowns: response.body,
      });
    });
  },

  bulkUpdateJobs(table, jobs, bulkUpdateData, options = {}) {
    APIRequest.abort(bulkUpdateJobsRequest);

    const staffRestriction = getRestrictionScope();

    AppDispatcher.dispatch({
      type:       JobActionTypes.BULK_UPDATE_JOBS,
      tableScope: table.scope,
      jobs,
      bulkUpdateData,
    });

    const scope = localStorage.ownerScope || staffRestriction || 'all';
    const updateData = {
      job_scope: table.scope,
      scope,
    };

    if (typeof jobs === 'string') {
      updateData.ids = 'all';
    } else {
      updateData.ids = _lodash.map(jobs, 'id');
    }

    const { page, s, per: size } = options;
    const data = {
      ...updateData, ...bulkUpdateData, s, scope,
    };

    bulkUpdateJobsRequest = APIRequest.put({
      resource: '/v1/job_bulk_actions/update_jobs',
      data,
    });

    bulkUpdateJobsRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_UPDATE_JOBS_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to update jobs', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_UPDATE_JOBS_DONE,
          tableScope: table.scope,
        });

        BulkSelectActions.deselectAllItems(table);

        GlobalContainer.notify(
          'Bulk action in progress, this may take some time.',
          'success',
        );

        JobActions.loadJobs(table, { page, size, searchData: s });
      }
    });
  },

  bulkDeleteJobs(table, jobs, bulkDeleteData, searchOptions) {
    APIRequest.abort(bulkDeleteJobsRequest);

    const staffRestriction = getRestrictionScope();
    const scope = localStorage.ownerScope || staffRestriction || 'all';
    const isAllJobs = typeof jobs === 'string';

    AppDispatcher.dispatch({
      type:       JobActionTypes.BULK_DELETE_JOBS,
      tableScope: table.scope,
      jobs,
      bulkDeleteData,
    });

    const deleteData = {
      job_scope: table.scope,
      scope,
      ids:       isAllJobs ? 'all' : _lodash.map(jobs, 'id'),
    };

    const { s } = searchOptions;
    const data = {
      ...deleteData, ...bulkDeleteData, s, scope,
    };

    bulkDeleteJobsRequest = APIRequest.delete({
      resource: '/v1/job_bulk_actions/delete_jobs',
      data,
    });

    bulkDeleteJobsRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_DELETE_JOBS_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to delete jobs', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_DELETE_JOBS_DONE,
          tableScope: table.scope,
        });

        BulkSelectActions.deselectAllItems(table);

        GlobalContainer.notify(
          'Bulk delete in progress, this may take some time.',
          'success',
        );
        JobActions.loadJobs(table);
      }
    });
  },

  switchJobScopeSize(widgets, jobScopeSize, searchData) {
    AppDispatcher.dispatch({
      type: JobActionTypes.SWITCH_JOB_SCOPE_SIZE,
      jobScopeSize,
    });
    localStorage.jobScopeSize = jobScopeSize;

    const table = widgets.tables[0];
    const options = { size: jobScopeSize, page: 1, searchData };
    if (table.scope === 'jobs') {
      JobActions.loadJobs(table, options);
    }
  },

  defaultCandidateOptions() {
    const staffRestriction = getRestrictionScope();

    return {
      ownerScope:    localStorage.ownerScope || staffRestriction || 'all',
      page:          1,
      size:          parseInt(localStorage.candidateScopeSize, 10) || 10,
      searchData:    {},
      sortColumn:    null,
      sortDirection: null,
      lead_scope:    'candidates',
    };
  },

  loadCandidates(table, options) {
    const opts = Object.assign(this.defaultCandidateOptions(), options);
    const {
      page,
      s,
      size,
      sortColumn,
      sortDirection,
      searchData,
      lead_scope,
      job_application_sort,
    } = opts;

    const jobId = table.jobId || options.jobId;
    if (!jobId) {
      return;
    }

    APIRequest.abort(loadCandidatesRequest);

    AppDispatcher.dispatch({
      type: JobActionTypes.LOAD_CANDIDATES,
      table,
    });

    const requestData = {
      job_scope: table.scope,
      page,
      per:       size,
      s:         s || searchData,
      lead_scope,
      jobId,
    };

    if (sortColumn && sortDirection) {
      requestData.job_application_sort = `${sortColumn}-${sortDirection}`;
    } else if (job_application_sort) {
      requestData.job_application_sort = job_application_sort;
    }

    loadCandidatesRequest = APIRequest.get({
      resource: `/v1/jobs/${jobId}/job_applications`,
      data:     requestData,
    });

    loadCandidatesRequest.end((error, response) => {
      if (error) {
        AppDispatcher.dispatch({
          type: JobActionTypes.LOAD_CANDIDATES_FAIL,
          table,
          error,
        });
        return;
      }

      AppDispatcher.dispatch({
        type:     JobActionTypes.LOAD_CANDIDATES_DONE,
        table,
        response: response.body,
        query:    requestData,
      });
    });
  },

  switchCandidateScopeSize(widgets, candidateScopeSize, searchData) {
    AppDispatcher.dispatch({
      type: JobActionTypes.SWITCH_CANDIDATE_SCOPE_SIZE,
      candidateScopeSize,
    });
    localStorage.candidateScopeSize = candidateScopeSize;

    const table = widgets.tables[0];
    const options = {
      size: candidateScopeSize,
      page: 1,
      searchData,
      s:    searchData,
    };
    if (table.scope === 'candidates') {
      JobActions.loadCandidates(table, options);
    }
  },


  bulkRemoveCandidatesFromJob(table, selectData, searchOptions) {
    APIRequest.abort(bulkRemoveCandidatesFromJobRequest);

    AppDispatcher.dispatch({
      type:       JobActionTypes.BULK_REMOVE_CANDIDATES_FROM_JOB,
      tableScope: table.scope,
    });

    const { jobId } = table;

    if (!jobId) {
      return;
    }

    const searchData = {
      job_scope: table.scope,
      ...searchOptions,
    };

    const data = {
      ...searchData,
      ...selectData,
    };

    bulkRemoveCandidatesFromJobRequest = APIRequest.delete({
      resource: `/v1/job_bulk_actions/${jobId}/delete_job_applications`,
      data,
    });

    bulkRemoveCandidatesFromJobRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_REMOVE_CANDIDATES_FROM_JOB_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to remove candidates from job', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_REMOVE_CANDIDATES_FROM_JOB_DONE,
          tableScope: table.scope,
        });

        BulkSelectActions.deselectAllItems(table);

        GlobalContainer.notify(
          'Bulk remove candidates from job in progress, this may take some time.',
          'success',
        );
        JobActions.loadCandidates(table, searchOptions);
      }
    });
  },

  bulkUpdateCandidatesJob(table, updateParams, searchData) {
    APIRequest.abort(bulkUpdateCandidatesFromJobRequest);

    AppDispatcher.dispatch({
      type:       JobActionTypes.BULK_UPDATE_CANDIDATES_FROM_JOB,
      tableScope: table.scope,
    });

    const { jobId } = table;

    if (!jobId) {
      return;
    }

    const requestData = {
      job_scope: table.scope,
      ...searchData,
    };

    const data = {
      ...requestData,
      ...updateParams,
    };

    bulkUpdateCandidatesFromJobRequest = APIRequest.put({
      resource: `/v1/job_bulk_actions/${jobId}/update_job_applications`,
      data,
    });

    bulkUpdateCandidatesFromJobRequest.end((error) => {
      if (error) {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_UPDATE_CANDIDATES_FROM_JOB_FAIL,
          tableScope: table.scope,
          error,
        });

        GlobalContainer.notify(error?.response?.body?.errors || 'Failed to update candidates in job', 'error');
      } else {
        AppDispatcher.dispatch({
          type:       JobActionTypes.BULK_UPDATE_CANDIDATES_FROM_JOB_DONE,
          tableScope: table.scope,
        });

        BulkSelectActions.deselectAllItems(table);

        GlobalContainer.notify(
          'Bulk update candidates in job in progress, this may take some time.',
          'success',
        );
        JobActions.loadCandidates(table, searchData);
      }
    });
  },

  /**
   * Load a candidate's lead by record index from job applications.
   * This method is used for navigation in the lead drawer for candidates.
   * @param {Object} tableQuery - Current table query object
   * @param {number} recordIndex - Load record at the index
   * @param {number} jobId - Job ID for the candidates table
   * @param {Function} onSuccess - Callback function to execute with the lead ID and record index
   */
  loadCandidateByIndex(tableQuery, recordIndex, jobId, onSuccess) {
    if (!jobId) {
      return;
    }

    const query = { ...tableQuery };
    // Ensure query.per is a valid positive integer before division
    if (!Number.isInteger(query.per) || query.per <= 0) {
      query.per = 10; // Default value for pagination
    }
    // calculate current page from recordIndex
    query.page = Math.floor(recordIndex / query.per) + 1;

    APIRequest.abort(loadCandidatesRequest);

    const requestData = {
      job_scope:            query.job_scope || 'candidates',
      page:                 query.page,
      per:                  query.per,
      s:                    query.s || {},
      lead_scope:           query.lead_scope || 'candidates',
      job_application_sort: query.job_application_sort || 'created_at-desc',
    };

    loadCandidatesRequest = APIRequest.get({
      resource: `/v1/jobs/${jobId}/job_applications`,
      data:     requestData,
    });

    loadCandidatesRequest.end((error, response) => {
      if (error) {
        return;
      }

      if (response.body.candidates && response.body.candidates.length > 0) {
        const candidateIndex = recordIndex % query.per;
        if (candidateIndex < response.body.candidates.length) {
          const candidateAtIndex = response.body.candidates[candidateIndex];
          if (candidateAtIndex && candidateAtIndex.lead_id && onSuccess) {
            onSuccess(candidateAtIndex.lead_id, recordIndex);
          }
        }
      }
    });
  },
};

export default JobActions;
